import type { SelectOption } from '@/components/AntdForm/antd-components.def.ts';
import {
  SCOPE_GEOGRAPHY_LABELS,
  SCOPE_GEOGRAPHY_ORDER,
  type AnalyticsScope,
  type AnalyticsScopeBody,
  type AnalyticsScopePatch,
  type DrillTarget,
  type ScopeGeography,
} from '@/features/analytics/analytics-scope.def.ts';
import { InvestorStatus } from '@/features/investor-management/investor-management.def.ts';

const LIST_KEYS = ['fundIds', 'shareClassIds', 'countries', 'legalNameCountries'] as const;
const SINGLE_KEYS = ['navAsOfMonth', 'status'] as const;

/** The pre-multi-select URL and wire key; read on load, removed on every scope write. */
export const LEGACY_GEOGRAPHY_PARAM = 'geography';

// Stored regions outside the three groups, mapped like backend `ToReportingGroup`.
const LEGACY_GEOGRAPHY_GROUPS: Readonly<Record<string, ScopeGeography>> = {
  NorthAmerica: 'Americas',
  LatinAmerica: 'Americas',
  Europe: 'Emea',
  MiddleEast: 'Emea',
  Africa: 'Emea',
};

/**
 * The reporting group for a group key or a legacy CLR key (`Europe` → `Emea`). With `acceptLabels`,
 * group display names (`'Americas (AMER)'`, the NAV row `geographyCode`) map too. Anything else is
 * undefined. `Object.hasOwn` keeps prototype keys such as `constructor` out.
 */
export const toScopeGeography = (
  value: string | null | undefined,
  { acceptLabels = false }: { acceptLabels?: boolean } = {},
): ScopeGeography | undefined => {
  if (!value) return undefined;
  const group = SCOPE_GEOGRAPHY_ORDER.find((key) => key === value);
  if (group) return group;
  if (Object.hasOwn(LEGACY_GEOGRAPHY_GROUPS, value)) return LEGACY_GEOGRAPHY_GROUPS[value];
  return acceptLabels
    ? SCOPE_GEOGRAPHY_ORDER.find((key) => SCOPE_GEOGRAPHY_LABELS[key] === value)
    : undefined;
};

/**
 * Appends the scope to a query string. Lists are repeated params (`?fundIds=a&fundIds=b`), which
 * RTK Query's `params` can't produce, so callers build the URL by hand. Empty values are omitted, so
 * an unscoped page sends no scope params at all.
 */
export const toScopeQueryString = (
  scope: AnalyticsScope | void,
  qs: URLSearchParams = new URLSearchParams(),
): URLSearchParams => {
  if (!scope) return qs;
  LIST_KEYS.forEach((key) => scope[key]?.forEach((value) => qs.append(key, value)));
  scope.geographies?.forEach((value) => qs.append('geographies', value));
  SINGLE_KEYS.forEach((key) => {
    const value = scope[key];
    if (value) qs.set(key, value);
  });
  return qs;
};

/** `path` plus the scope query string, or bare `path` when the scope is empty. */
export const withScopeQuery = (path: string, scope: AnalyticsScope | void): string => {
  const queryString = toScopeQueryString(scope).toString();
  return queryString ? `${path}?${queryString}` : path;
};

/**
 * The scope fields for a grid POST body, flat at the top level (backend `AnalyticsScopeGridRequestBase`).
 * Only the scope keys are copied, so grid args or a route `fundId` spread into the same object
 * never leak into the body; empty values are omitted.
 */
export const toScopeBody = (scope: AnalyticsScope | void): AnalyticsScopeBody => {
  const body: AnalyticsScopeBody = {};
  if (!scope) return body;
  LIST_KEYS.forEach((key) => {
    const values = scope[key];
    if (values?.length) body[key] = [...values];
  });
  if (scope.geographies?.length) body.geographies = [...scope.geographies];
  if (scope.navAsOfMonth) body.navAsOfMonth = scope.navAsOfMonth;
  if (scope.status) body.status = scope.status;
  return body;
};

/** Status options for scope controls: value is the enum key, label the display string. */
export const scopeInvestorStatusOptions: SelectOption[] = Object.entries(InvestorStatus).map(
  ([value, label]) => ({ label, value }),
);

/**
 * Geography options in report order: the groups observed for the current scope plus any already
 * selected, so a selection the data no longer shows stays visible and removable.
 */
export const scopeGeographyOptions = (
  observed: readonly string[] | undefined,
  selected: readonly ScopeGeography[] = [],
): SelectOption[] => {
  const present = new Set<string>([...(observed ?? []), ...selected]);
  return SCOPE_GEOGRAPHY_ORDER.filter((key) => present.has(key)).map((value) => ({
    value,
    label: SCOPE_GEOGRAPHY_LABELS[value],
  }));
};

/**
 * The current scope with a drill's changes applied. Every scope dimension the patch names is
 * replaced (lists are not merged); `undefined` or an empty list removes it. Untouched dimensions,
 * including `navAsOfMonth`, carry over, so a drill never silently changes the valuation date (L3.2-08).
 */
export const mergeScopePatch = (
  scope: AnalyticsScope,
  patch: AnalyticsScopePatch = {},
): AnalyticsScope => {
  const merged: AnalyticsScope = { ...scope, ...patch };
  [...LIST_KEYS, 'geographies' as const, ...SINGLE_KEYS].forEach((key) => {
    const value = merged[key];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) delete merged[key];
  });
  return merged;
};

/** The URL a drill opens: the target path with the merged scope as its query string. */
export const drillHref = (target: DrillTarget, scope: AnalyticsScope): string =>
  withScopeQuery(target.pathname, mergeScopePatch(scope, target.patch));
